import {
  Flex,
  Skeleton,
  Stack,
  Title,
  VisuallyHidden,
  desktopFirstMediaQuery,
  desktopFirstQuery,
} from '@inflearn/ds-react-next';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'next-i18next';
import type { ReactElement } from 'react';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { getCdnTranslationProps } from '../../../utils/server/i18n/getCdnTranslationProps';

import { ListBoundary } from '../../../components/common/ListBoundary';
import { UrlStateSelect } from '../../../components/common/UrlStateSelect';
import { MyCoursesLayout } from '../../../components/layout/MyPageLayout/MyCoursesLayout';
import { NoteList } from '../../../components/pages/my/notes/NoteList';
import { NoteSearchBar } from '../../../components/pages/my/notes/NoteSearchBar';
import type { NoteSort } from '../../../components/pages/my/notes/types';

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await getCdnTranslationProps(locale, ['common'])),
  },
  revalidate: 60,
});

type SortOption = {
  label: string;
  value: NoteSort;
};

const NoteListSkeleton = () => (
  <Stack gap={16}>
    {Array.from({ length: 7 }).map((_, index) => (
      <Skeleton w="100%" h={80} radius="md" key={index} />
    ))}
  </Stack>
);

export default function NotesPage() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(desktopFirstQuery.mobile);

  const NOTE_SORTS: SortOption[] = [
    { label: t('최근 학습순'), value: 'STUDIED' },
    { label: t('수강신청 순'), value: 'ENROLLED' },
  ];

  return (
    <>
      <VisuallyHidden>
        <Title order={2} pb={8} mb={16}>
          {t('강의 노트')}
        </Title>
      </VisuallyHidden>
      <Flex
        w="100%"
        mt={24}
        mb={24}
        gap={8}
        styles={{
          root: { [desktopFirstMediaQuery.mobile]: { flexDirection: 'column' } },
        }}
      >
        <NoteSearchBar isMobile={isMobile} />
        <UrlStateSelect options={NOTE_SORTS} isMobile={isMobile} />
      </Flex>
      <ListBoundary
        fallback={
          <DeferredComponent>
            <NoteListSkeleton />
          </DeferredComponent>
        }
      >
        <NoteList />
      </ListBoundary>
    </>
  );
}

function NotesPageLayout({ children, pathname }: { children: ReactElement; pathname: string }) {
  const { t } = useTranslation();
  return (
    <MyCoursesLayout
      pathname={pathname}
      title={t('메타.강의노트.제목')}
      description={t('메타.강의노트.설명')}
    >
      {children}
    </MyCoursesLayout>
  );
}

NotesPage.getLayout = (page: ReactElement, pathname: string) => (
  <NotesPageLayout pathname={pathname}>{page}</NotesPageLayout>
);
